import { useTheme } from '@/contexts/ThemeContext'
import type { IndexDef } from '@/api/types/readiness'
import { findObjective } from '@/lib/indexes/registry'
import { REVIEW_REASON_LABEL, levelCode, type AssessmentCell } from '@/api/clients/assessmentsApi'
import { RdModal } from '@/components/readiness/RdModal'
import { DomainDot, Icon } from '@/components/readiness/primitives'

// ── Review changes ──────────────────────────────────────────────────────────
// A sync never changes a level: it suggests one, and the objective waits for
// the Assessor. The banner says how many objectives wait and stays until each
// is decided, so closing the list loses nothing; the list opens each
// objective's drawer, where the level is kept or the suggestion accepted.

const MODULE_LABEL = { discovery: 'Discovery', playbook: 'Playbook' } as const

export function ReviewChangesBanner({ cells, onReview }: { cells: AssessmentCell[]; onReview: () => void }) {
  if (!cells.length) return null
  const suggested = cells.filter((c) => c.review_reason === 'suggestion').length
  const n = cells.length
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-[10px] px-4 py-2.5 text-[12.5px]" style={{ background: 'var(--rd-status-warn-bg)', color: 'var(--rd-text-secondary)' }} role="status" data-testid="review-changes-banner">
      <Icon name="sync" size={14} style={{ color: 'var(--rd-status-warn)' }} />
      <span>
        {n} {n === 1 ? 'objective needs' : 'objectives need'} your review
        {suggested ? ` · ${suggested} with a new suggested level from Discovery or Playbook` : ''}. Levels you set stay as they are until you decide.
      </span>
      <button type="button" className="rd-btn rd-btn-sm rd-btn-primary ml-auto" onClick={onReview} data-testid="review-changes-open">Review changes ({n})</button>
    </div>
  )
}

export function ReviewChangesModal({ index, cells, onOpen, onClose }: { index: IndexDef; cells: AssessmentCell[]; onOpen: (code: string) => void; onClose: () => void }) {
  const { theme } = useTheme()
  return (
    <RdModal
      title={`Review changes · ${cells.length} ${cells.length === 1 ? 'objective' : 'objectives'}`}
      subtitle="Open an objective to keep your level or accept the suggestion."
      width={760}
      onClose={onClose}
      testId="review-changes-modal"
      bodyClassName="p-0"
      footer={<button type="button" className="rd-btn" onClick={onClose}>Close</button>}
      footerClassName="justify-end"
    >
      {cells.length === 0 ? (
        <p className="px-6 py-8 text-center text-[13px]" style={{ color: 'var(--rd-text-muted)' }}>Nothing waits for review.</p>
      ) : (
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[12px]" style={{ color: 'var(--rd-text-muted)', background: 'var(--rd-bg-header)' }}>
              <th className="px-6 py-2 font-semibold">Objective</th>
              <th className="py-2 font-semibold">Why</th>
              <th className="py-2 font-semibold">Your level</th>
              <th className="py-2 pr-6 font-semibold">Suggested</th>
            </tr>
          </thead>
          <tbody>
            {cells.map((c) => {
              const f = findObjective(index, c.objective_code)
              const reason = c.review_reason ?? 'suggestion'
              return (
                <tr key={c.objective_code} className="rd-qv-row cursor-pointer" style={{ borderTop: '1px solid var(--rd-border-subtle)' }} onClick={() => onOpen(c.objective_code)} data-testid={`review-row-${c.objective_code}`}>
                  <td className="px-6 py-2.5">
                    <button type="button" className="flex items-center gap-2 text-left" onClick={(e) => { e.stopPropagation(); onOpen(c.objective_code) }}>
                      {f && <DomainDot domain={f.domain} theme={theme} size={10} />}
                      <span className="rd-mono rd-id font-semibold">{c.objective_code}</span>
                      <span className="min-w-0 truncate" style={{ maxWidth: 220 }} title={c.objective_title}>{c.objective_title}</span>
                    </button>
                  </td>
                  <td className="py-2.5" style={{ color: 'var(--rd-text-secondary)' }}>
                    {REVIEW_REASON_LABEL[reason]}
                    {reason === 'suggestion' && c.suggestion_source ? <span style={{ color: 'var(--rd-text-faint)' }}> · {MODULE_LABEL[c.suggestion_source]}</span> : null}
                  </td>
                  <td className="rd-mono py-2.5">{levelCode(c.level) ?? 'Not set'}</td>
                  <td className="rd-mono py-2.5 pr-6">{reason === 'suggestion' ? levelCode(c.suggested_level) : '—'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </RdModal>
  )
}
